/**
 * VOICE-001 — a voice note turned into a draft quote.
 *
 * Everything the browser and the API both need to agree on lives here. The
 * proposal is deliberately not a quote: nothing in it has been saved, numbered
 * or audited, and the editor is where it becomes one — through the same routes
 * a typed quote takes.
 */
/**
 * `dictate` is speech to text only — the words of one field, no model reading
 * them and no proposal — so it is never counted against the monthly quota.
 */
export const VOICE_JOB_KINDS = ['create_quote', 'edit_quote', 'dictate'] as const;
export type VoiceJobKind = (typeof VOICE_JOB_KINDS)[number];

export const VOICE_JOB_STATUSES = [
  'queued',
  'transcribing',
  'understanding',
  'ready',
  'failed',
  'cancelled',
] as const;
export type VoiceJobStatus = (typeof VOICE_JOB_STATUSES)[number];

/** A job in one of these never changes again. */
export const VOICE_TERMINAL_STATUSES: readonly VoiceJobStatus[] = ['ready', 'failed', 'cancelled'];

/**
 * Where a line's price came from — the editor marks `history` and `missing`.
 * `included` is a work inside a job whose total is on the job's line: priced
 * 0 on purpose, and printed as "included".
 */
export type VoicePriceSource = 'spoken' | 'history' | 'missing' | 'included';

export interface VoiceProposalItem {
  description: string;
  quantity: number;
  unitPrice: number | null;
  priceSource: VoicePriceSource;
  /** The price is not a number that was said, or a said amount went unused. */
  amountMismatch: boolean;
}

export interface VoiceCustomerCandidate {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  city: string | null;
}

export type VoiceCustomerMatch =
  | { match: 'exact'; customer: VoiceCustomerCandidate }
  | { match: 'candidates'; spokenName: string; candidates: VoiceCustomerCandidate[] }
  | { match: 'none'; spokenName: string | null };

/**
 * Positions are zero-based indexes into the rows sent with the job. An update's
 * `amountMismatch` means the same as a line's: the new price is not a number
 * that was said, alone or times the row's quantity. It is false when the update
 * leaves the price alone.
 */
export type VoiceEdit =
  | { op: 'add'; item: VoiceProposalItem }
  | {
      op: 'update';
      position: number;
      /** The line's new wording. An app older than this field ignores it. */
      description?: string;
      quantity?: number;
      unitPrice?: number;
      amountMismatch: boolean;
    }
  | { op: 'remove'; position: number };

/**
 * The rest of the quote, as said. Every field is something the owner said;
 * one left out was not said, and the editor leaves it as it is.
 *
 * Optional as a whole: a proposal stored before it existed has none, and an
 * app older than it never reads it.
 */
export interface VoiceQuoteFields {
  /**
   * The customer's details as said. On a new quote they sit over whatever the
   * matched card holds — the owner said them now; on an edit they change the
   * quote's customer, `name` included.
   */
  customer?: { name?: string; phone?: string; email?: string; city?: string };
  /**
   * Where the work is done. The quote has no field of its own for it, so the
   * editor writes it as a line of the notes-and-terms text, worded from the
   * dictionary — the API has no locale to word it in.
   */
  workAddress?: string;
  /** A note the owner asked to add, as said; added under what the text holds. */
  note?: string;
  /** The body of the business's saved terms the owner asked to add. */
  terms?: string;
  /** `none` takes a discount off. */
  discount?: { type: 'percent' | 'amount' | 'none'; value: number; label?: string };
  /** 0 for a VAT-exempt dealer; any other rate is not taken from speech. */
  vatRate?: number;
  validDays?: 1 | 7 | 14 | 30;
  /** `null` is the business's own threshold, as the editor's first button. */
  followUpHours?: 12 | 24 | 48 | null;
}

export interface VoiceProposal {
  kind: VoiceJobKind;
  customer: VoiceCustomerMatch;
  title: string | null;
  /** `null` — nothing was said, so the business default stays. */
  pricesIncludeVat: boolean | null;
  items: VoiceProposalItem[];
  edits: VoiceEdit[];
  fields?: VoiceQuoteFields;
}

export interface VoiceJob {
  id: string;
  kind: VoiceJobKind;
  quoteId: string | null;
  status: VoiceJobStatus;
  /** 1-based place in the host-wide queue while `queued`, otherwise null. */
  queuePosition: number | null;
  transcript: string | null;
  proposal: VoiceProposal | null;
  errorKey: string | null;
  createdAt: string;
}
